import React, { useState, useEffect } from "react";
import {
  Table,
  Segment,
  Header,
  Icon,
  Message,
  Button,
  Input,
  Loader,
  Popup,
} from "semantic-ui-react";
import {
  CONVERSION_FACTORS_TO_MWH,
  FUELS,
} from "../../../../utils/dataAndFormulas/convertEnergy";
import { ConversionFactor } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { toast } from "react-toastify";
import "./ConversionFactorsTable.scss";

const conversionFactorController = new ConversionFactor();

const DEFAULT_FACTORS_TO_KWH = {
  electricity: 1,
  renewable_energies: 1,
  natural_gas: 9.33,
  glp_l: 7.10,
  glp_kg: 13.14,
  heavy_fuel_oil: 11.63,
  light_fuel_oil: 12.14,
  coal: 4.66,
  diesel: 12.00,
  biodiesel: 12.00,
  gasoline: 12.39,
  biofuel: 12.39,
  biomass: 1.68,
  steam: 0.69,
};

export function ConversionFactorsTable({ site }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [initialized, setInitialized] = useState(false);

  const fuelKeys = Object.keys(CONVERSION_FACTORS_TO_MWH);

  useEffect(() => {
    if (site && accessToken) {
      loadFactors();
    }
  }, [site, accessToken]);

  const loadFactors = async () => {
    setLoading(true);
    try {
      const response = await conversionFactorController.getFactorsBySite(
        accessToken,
        site
      );
      if (response.code === 200) {
        setFactors(response.factors);
        setInitialized(response.factors.length > 0);
      }
    } catch (error) {
      console.error("Error al cargar factores:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    setLoading(true);
    try {
      const response = await conversionFactorController.initializeDefaultFactors(
        accessToken,
        site
      );
      if (response.code === 201) {
        toast.success(t("conversion_factors_initialized"));
        await loadFactors();
      }
    } catch (error) {
      console.error("Error al inicializar:", error);
      toast.error("Error al inicializar factores");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t("conversion_factors_reset_confirm"))) return;
    setLoading(true);
    try {
      const response = await conversionFactorController.resetToDefaults(
        accessToken,
        site
      );
      if (response.code === 200) {
        toast.success(t("conversion_factors_reset_success"));
        setFactors(response.factors);
        setEditing(null);
      }
    } catch (error) {
      console.error("Error al restaurar:", error);
      toast.error("Error al restaurar factores");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (factor) => {
    setEditing(factor._id);
    setEditValues({
      factorToKWh: factor.factorToKWh,
      factorToMWh: factor.factorToMWh,
    });
  };

  const handleSave = async (factorId) => {
    setLoading(true);
    try {
      const response = await conversionFactorController.updateFactor(
        accessToken,
        factorId,
        editValues
      );
      if (response.code === 200) {
        toast.success(t("conversion_factor_updated"));
        setEditing(null);
        await loadFactors();
      }
    } catch (error) {
      console.error("Error al actualizar:", error);
      toast.error("Error al actualizar factor");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(null);
    setEditValues({});
  };

  const handleResetSingle = async (factor) => {
    const defaultKWh = DEFAULT_FACTORS_TO_KWH[factor.fuelType];
    const defaultMWh = CONVERSION_FACTORS_TO_MWH[factor.fuelType];
    if (defaultKWh === undefined && defaultMWh === undefined) return;
    if (!window.confirm(`¿Restaurar "${t(factor.fuelType)}" a sus valores por defecto?`)) return;
    setLoading(true);
    try {
      const response = await conversionFactorController.updateFactor(
        accessToken,
        factor._id,
        { factorToKWh: defaultKWh ?? factor.factorToKWh, factorToMWh: defaultMWh ?? factor.factorToMWh }
      );
      if (response.code === 200) {
        toast.success(t("conversion_factor_updated"));
        await loadFactors();
      }
    } catch (error) {
      console.error("Error al restaurar factor:", error);
      toast.error("Error al restaurar el factor");
    } finally {
      setLoading(false);
    }
  };

  // Construir mapa de factores de la BD indexado por fuelType
  const factorMap = {};
  factors.forEach((f) => {
    factorMap[f.fuelType] = f;
  });

  if (!site) {
    return (
      <Message warning>
        <Message.Header>{t("select_site")}</Message.Header>
        <p>{t("select_site_conversion_factors")}</p>
      </Message>
    );
  }

  return (
    <div className="conversion-factors-table">
      <Segment>
       
{/* 
        <Message info>
          <Message.Header>{t("conversion_factors_info_title")}</Message.Header>
          <p>{t("conversion_factors_info")}</p>
        </Message> */}

        {!initialized && !loading && (
          <div style={{ marginBottom: "15px" }}>
            <Button
              primary
              icon
              labelPosition="left"
              onClick={handleInitialize}
              disabled={loading}
            >
              <Icon name="download" />
              {t("initialize_default_factors")}
            </Button>
          </div>
        )}

        {initialized && (
          <div style={{ marginBottom: "15px", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            <Popup
              content={t("reload")}
              position="top center"
              trigger={
                <Button className="cf-btn cf-btn-ghost" onClick={loadFactors} disabled={loading}>
                  <Icon name="refresh" />
                  {t("reload") || "Recargar"}
                </Button>
              }
            />
            <Popup
              content={t("reset_to_defaults")}
              position="top center"
              trigger={
                <Button className="cf-btn cf-btn-azure" onClick={handleReset} disabled={loading}>
                  <Icon name="undo" />
                  {t("reset_to_defaults") || "Restaurar"}
                </Button>
              }
            />
          </div>
        )}

        {loading && <Loader active inline="centered" />}

        <Table className="cf-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("fuel_source")}</Table.HeaderCell>
              <Table.HeaderCell>{t("unit")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">
                {t("factor_to_kwh")}
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center">
                {t("factor_to_mwh")}
              </Table.HeaderCell>
              {initialized && (
                <Table.HeaderCell textAlign="center">
                  {t("actions")}
                </Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {fuelKeys.map((key) => {
              const fuel = FUELS[key] || {};
              const dbFactor = factorMap[key];
              const isEditing = dbFactor && editing === dbFactor._id;

              // Usar valores de BD si existen, sino los hardcodeados
              const kwh = dbFactor
                ? dbFactor.factorToKWh
                : DEFAULT_FACTORS_TO_KWH[key] ?? "-";
              const mwh = dbFactor
                ? dbFactor.factorToMWh
                : CONVERSION_FACTORS_TO_MWH[key] ?? "-";

              return (
                <Table.Row key={key}>
                  <Table.Cell>
                    <strong>{t(key)}</strong>
                  </Table.Cell>
                  <Table.Cell>{fuel.unit ? t(fuel.unit) : "-"}</Table.Cell>
                  <Table.Cell textAlign="center">
                    {isEditing ? (
                      <Input
                        size="mini"
                        type="number"
                        step="0.00001"
                        value={editValues.factorToKWh}
                        onChange={(e) =>
                          setEditValues({
                            ...editValues,
                            factorToKWh: parseFloat(e.target.value),
                          })
                        }
                        style={{ width: "120px" }}
                      />
                    ) : (
                      kwh
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {isEditing ? (
                      <Input
                        size="mini"
                        type="number"
                        step="0.00001"
                        value={editValues.factorToMWh}
                        onChange={(e) =>
                          setEditValues({
                            ...editValues,
                            factorToMWh: parseFloat(e.target.value),
                          })
                        }
                        style={{ width: "120px" }}
                      />
                    ) : (
                      mwh
                    )}
                  </Table.Cell>
                  {initialized && (
                    <Table.Cell textAlign="center">
                      {dbFactor &&
                        (isEditing ? (
                          <>
                            <Button
                              icon
                              size="small"
                              color="green"
                              onClick={() => handleSave(dbFactor._id)}
                              disabled={loading}
                            >
                              <Icon name="check" />
                            </Button>
                            <Button
                              icon
                              size="small"
                              onClick={handleCancel}
                            >
                              <Icon name="cancel" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              icon
                              size="small"
                              onClick={() => handleEdit(dbFactor)}
                              disabled={!!editing}
                              title={t("edit")}
                            >
                              <Icon name="edit" />
                            </Button>
                            <Button
                              icon
                              size="small"
                              color="orange"
                              onClick={() => handleResetSingle(dbFactor)}
                              disabled={!!editing || loading}
                              title={t("reset_to_defaults")}
                            >
                              <Icon name="undo" />
                            </Button>
                          </>
                        ))}
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Segment>
    </div>
  );
}
